import React, { useState, useEffect, useMemo, useCallback } from "react";
import DatePicker from "react-datepicker";
import { hr } from "date-fns/locale";
import { format, parse } from "date-fns";
import "react-datepicker/dist/react-datepicker.css";
import "./NalogUser.css";
import { ReportAPI } from "../../Api/ReportAPI";

function convertToLocalDate(reportDate) {
  const parsed = parse(reportDate, "dd-MM-yyyy", new Date());
  if (isNaN(parsed)) {
    console.warn("⚠️ could not parse date:", reportDate);
    return new Date(0);
  }
  return parsed;
}

const ReportCalendar = ({ selectedDate, setSelectedDate, availableDates }) => (
  <div className="calendar-container">
    <label htmlFor="report-date">Odaberite datum: </label>
    <DatePicker
      id="report-date"
      selected={selectedDate}
      onChange={setSelectedDate}
      includeDates={availableDates}
      locale={hr}
      dateFormat="dd MMMM yyyy"
      placeholderText="Select a report date"
      className="custom-datepicker"
    />
  </div>
);

const NalogUser = () => {
  const [reports, setReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [reportsError, setReportsError] = useState(null);
  const [reportFilePdf, setReportFilePdf] = useState(null);
  const [iframeKey, setIframeKey] = useState(0);
  const [selectedReport, setSelectedReport] = useState(null);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);

  const handleDownloadAll = useCallback(async () => {
    try {
      const response = await ReportAPI.downloadAllReports();
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "svi_reportovi.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Greška prilikom preuzimanja reportova:", error);
    }
  }, []);

  const handlePrintAll = useCallback(async () => {
    try {
      const response = await ReportAPI.printAllReports();
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (error) {
      console.error("Greška prilikom ispisa reportova:", error);
    }
  }, []);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        const response = await ReportAPI.getNalogReports();
        const fetchedReports = response.data || [];

        setReports(fetchedReports);

        if (fetchedReports.length > 0) {
          setSelectedReport(fetchedReports[0]);
          setSelectedCalendarDate(convertToLocalDate(fetchedReports[0].date));
        }
      } catch (err) {
        console.error(err);
        setReportsError("Došlo je do pogreške prilikom dohvaćanja naloga.");
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    let objectUrl = null;

    if (!selectedReport) return;

    ReportAPI.getReportFile(selectedReport.id, 0)
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        objectUrl = URL.createObjectURL(blob);
        setReportFilePdf(objectUrl);
      })
      .catch((error) => {
        console.error("Greška pri učitavanju PDF-a:", error);
      });

    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedReport]);

  const availableDates = useMemo(() => {
    return Array.from(new Set(reports.map((r) => convertToLocalDate(r.date).getTime())))
      .map((t) => new Date(t))
      .sort((a, b) => b.getTime() - a.getTime());
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (!selectedCalendarDate) return [];
    return reports.filter(
      (r) => convertToLocalDate(r.date).getTime() === selectedCalendarDate.getTime()
    );
  }, [reports, selectedCalendarDate]);

  useEffect(() => {
    if (filteredReports.length > 0) {
      setSelectedReport(filteredReports[0]);
      setIframeKey((k) => k + 1);
    } else {
      setSelectedReport(null);
      setReportFilePdf(null);
    }
  }, [filteredReports]);

  const handleReportClick = useCallback((rpt) => {
    setSelectedReport(rpt);
    setIframeKey((k) => k + 1);
  }, []);

  const isMobile = window.innerWidth < 768;

  return (
    <div className="container">
      <div className="header">
        <h2>Nalozi</h2>
        {selectedCalendarDate && (
          <ReportCalendar
            selectedDate={selectedCalendarDate}
            setSelectedDate={setSelectedCalendarDate}
            availableDates={availableDates}
          />
        )}
        {filteredReports.length > 0 && selectedReport && (
          <span>
            {format(convertToLocalDate(selectedReport.date), "dd MMMM yyyy")} u{" "}
            {selectedReport.time} ({selectedReport.location})
          </span>
        )}
      </div>

      {loadingReports ? (
        <div className="spinner-container">
          <div className="spinner"></div>
          <p>Učitavanje izvještaja...</p>
        </div>
      ) : reportsError ? (
        <div className="error-message">
          <p>{reportsError}</p>
        </div>
      ) : (
        <>
          {filteredReports.length === 0 ? (
            <div className="no-report-message">
              Još uvijek ne postoji izvještaj za taj dan
            </div>
          ) : (
            <>
              <div className="date-list">
                {filteredReports.map((rpt) => (
                  <div
                    key={rpt.id}
                    className={`date-item ${selectedReport?.id === rpt.id ? "selected" : ""}`}
                    onClick={() => handleReportClick(rpt)}
                    role="button"
                    tabIndex="0"
                  >
                    {rpt.date} {rpt.time} ({rpt.location})
                  </div>
                ))}
              </div>

              {/*
              <div className="buttons-container">
                <button className="preuzmi" onClick={handleDownloadAll}>
                  Preuzmi sve naloge
                </button>
                <button className="ispisi" onClick={handlePrintAll}>
                  Ispiši sve naloge
                </button>
              </div>
              */}

              <div className="iframe-container">
                {isMobile ? (
                  <div className="mobile-report">
                    <p>PDF pregled nije podržan na mobilnom uređaju.</p>
                    {reportFilePdf ? (
                      <a href={reportFilePdf} target="_blank" rel="noopener noreferrer">
                        Kliknite ovdje za preuzimanje izvještaja
                      </a>
                    ) : null}
                  </div>
                ) : (
                  selectedReport &&
                  reportFilePdf && (
                    <iframe
                      key={iframeKey}
                      title={`Report for ${selectedReport.date} at ${selectedReport.time}`}
                      src={reportFilePdf}
                      width="100%"
                      height="1200px"
                    />
                  )
                )}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default NalogUser;